// =============================================================================
// MeuDriver — Hook: useSupportTickets (Batch R4)
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import type {
  SupportTicketRow,
  SupportTicketInsert,
  SupportTicketUpdate,
  SupportTicketMessageRow,
  SupportTicketStatus,
} from '@/types/database'

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const ticketKeys = {
  myTickets:      ()                      => ['support_tickets', 'mine']            as const,
  adminTickets:   (status?: string)       => ['support_tickets', 'admin', status]   as const,
  messages:       (ticketId: string)      => ['support_ticket_messages', ticketId]  as const,
}

// ---------------------------------------------------------------------------
// useMyTickets — tickets do usuário autenticado
// ---------------------------------------------------------------------------

export function useMyTickets() {
  const { user } = useAuth()

  return useQuery<SupportTicketRow[]>({
    queryKey: ticketKeys.myTickets(),
    enabled: !!user?.id,
    staleTime: 1000 * 60,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('support_tickets')
        .select('*')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false })

      if (error) throw error
      return (data ?? []) as SupportTicketRow[]
    },
  })
}

// ---------------------------------------------------------------------------
// useCreateTicket
// ---------------------------------------------------------------------------

export function useCreateTicket() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (args: { subject: string; description: string; priority?: SupportTicketRow['priority'] }) => {
      if (!user?.id) throw new Error('Usuário não autenticado')

      const insert: SupportTicketInsert = {
        user_id:     user.id,
        subject:     args.subject,
        description: args.description,
        priority:    args.priority ?? 'medium',
      }

      const { data, error } = await supabase
        .from('support_tickets')
        .insert(insert)
        .select()
        .single()

      if (error) throw error
      return data as SupportTicketRow
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.myTickets() })
    },
  })
}

// ---------------------------------------------------------------------------
// useTicketMessages
// ---------------------------------------------------------------------------

export function useTicketMessages(ticketId: string) {
  return useQuery<SupportTicketMessageRow[]>({
    queryKey: ticketKeys.messages(ticketId),
    enabled: !!ticketId,
    staleTime: 1000 * 30,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('support_ticket_messages')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true })

      if (error) throw error
      return (data ?? []) as SupportTicketMessageRow[]
    },
  })
}

// ---------------------------------------------------------------------------
// useSendTicketMessage
// ---------------------------------------------------------------------------

export function useSendTicketMessage() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (args: { ticketId: string; body: string }) => {
      if (!user?.id) throw new Error('Usuário não autenticado')

      const { data, error } = await supabase
        .from('support_ticket_messages')
        .insert({
          ticket_id:     args.ticketId,
          sender_id:     user.id,
          body:          args.body,
          is_staff_reply: false,
        })
        .select()
        .single()

      if (error) throw error
      return data as SupportTicketMessageRow
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.messages(data.ticket_id) })
    },
  })
}

// ---------------------------------------------------------------------------
// useAdminTickets — lista todos os tickets (admin)
// ---------------------------------------------------------------------------

export function useAdminTickets(status?: SupportTicketStatus) {
  return useQuery<SupportTicketRow[]>({
    queryKey: ticketKeys.adminTickets(status),
    staleTime: 1000 * 30,
    queryFn: async () => {
      let query = supabase
        .from('support_tickets')
        .select('*')
        .order('updated_at', { ascending: false })

      if (status) {
        query = query.eq('status', status)
      }

      const { data, error } = await query
      if (error) throw error
      return (data ?? []) as SupportTicketRow[]
    },
  })
}

// ---------------------------------------------------------------------------
// useUpdateTicketStatus — admin muda status/prioridade via RPC (ALTO-005)
// UPDATE direto não funciona: REVOKE UPDATE atua antes do RLS, bloqueando
// até admins. A RPC admin_update_support_ticket usa SECURITY DEFINER e
// verifica role internamente.
// ---------------------------------------------------------------------------

export function useUpdateTicketStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (args: { id: string; status?: SupportTicketRow['status']; priority?: SupportTicketRow['priority'] }) => {
      const { error } = await supabase.rpc('admin_update_support_ticket', {
        p_ticket_id: args.id,
        p_status:    args.status    ?? null,
        p_priority:  args.priority  ?? null,
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support_tickets'] })
    },
  })
}

// ---------------------------------------------------------------------------
// useCloseTicket — owner fecha o próprio ticket via RPC (ALTO-005)
// Usa close_support_ticket RPC que atualiza apenas status e updated_at,
// impedindo adulteração de subject/description/priority.
// ---------------------------------------------------------------------------

export function useCloseTicket() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (ticketId: string) => {
      const { error } = await supabase.rpc('close_support_ticket', {
        p_ticket_id: ticketId,
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support_tickets'] })
    },
  })
}
